import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import Select from 'react-select';
import { IoMdClose } from 'react-icons/io';
import { BASE_URL } from "../../../../services/Api";
import { authHeader } from "../../../../services/auth-header";
import debounce from 'lodash/debounce';
import CommonSalesEnquiryFields from '../CommonSalesEnquiryFields';

function EditPumpInternationalSalesEnquiry() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [status, setstatus] = useState('');
    const [customermaterialcode, setcustomermaterialcode] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productOptions, setProductOptions] = useState([]);
    const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
    const [rows, setRows] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const [commonFormData, setCommonFormData] = useState({});

    const unitOptions = [
        { value: "", label: "Select UOM" },
        { value: "bot", label: "Bot" },
        { value: "ctn", label: "Ctn" },
        { value: "days", label: "Days" },
        { value: "ft", label: "Ft" },
        { value: "hrs", label: "Hrs" },
        { value: "kg", label: "Kg" },
        { value: "ltr", label: "Ltr" },
        { value: "mtr", label: "Mtr" },
        { value: "nos", label: "Nos" },
        { value: "pil", label: "Pil" },
        { value: "pkt", label: "Pkt" },
        { value: "set", label: "Set" },
        { value: "pcs", label: "Pcs" }
    ];


    const currencyOptions = [
        { value: "", label: "Select Currency" },
        { value: "$ (USD)", label: "USD" },
        { value: "€ (EUR)", label: "EUR" },
        { value: "¥ (JPY)", label: "JPY" },
        { value: "£ (GBP)", label: "GBP" },
        { value: "₹ (INR)", label: "INR" },


    ];

    const [enquirydate, setEnquiryDate] = useState('');
    const [enquiryno, setenquiryno] = useState('');


    useEffect(() => {
        const fetchEnquiryData = async () => {
            try {
                const response = await axios.get(`${BASE_URL}enquiry/findById/${id}`, {
                    headers: authHeader()
                });
                const enquiry = response.data;
                const firstItemCurrency = enquiry.enquiryItems?.[0]?.currency || '';
                setEnquiryDate(enquiry.enquirydate);
                setenquiryno(enquiry.enquiryno);
                setstatus(enquiry.status);
                setcustomermaterialcode(enquiry.customermaterialcode || '');
                setSelectedCurrency(firstItemCurrency);

                // Set common form data
                setCommonFormData({
                    customer: enquiry.customer ? {
                        value: enquiry.customer.id,
                        label: enquiry.customer.companyname
                    } : null,
                    customerDetails: {
                        contactperson: enquiry.contactperson,
                        contactno: enquiry.contactno,
                        email: enquiry.email
                    },
                    customerMultipleAddress: enquiry.customeraddress ? {
                        value: enquiry.customeraddress.id,
                        label: enquiry.customeraddress.customeraddress
                    } : null,
                });

                // Set existing items
                if (enquiry.enquiryItems && enquiry.enquiryItems.length > 0) {
                    const mappedItems = enquiry.enquiryItems.map(item => ({
                        description: item.product?.description,
                        value: item.product?.id,
                        label: `${item.product?.description} - ${item.product?.serialnumber} - ${item.product?.partno}`,
                        quantity: item.quantity,
                        partno: item.partno || item.product?.partno,
                        hsnsacno: item.hsnsacno || item.product?.hsnsacno,
                        itemcode: item.itemcode || item.product?.itemcode,
                        serialnumber: item.serialnumber || item.product?.serialnumber,
                        modelnumber: item.modelnumber || item.product?.modelnumber,
                        tagnumber: item.tagnumber || item.product?.tagnumber,
                        moc: item.moc || item.product?.moc,
                        uom: item.uom || item.product?.unitofmeasurement || unitOptions[0].value,
                        productremarks: item.productremarks || "",
                        currency: item.currency || firstItemCurrency,
                        id: item.id
                    }));
                    setRows(mappedItems);
                }
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load enquiry data. Please try again.'
                });
                console.error('Error fetching enquiry:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchEnquiryData();
    }, [id]);

    const handleProductSearchChange = debounce((inputValue) => {
        setProductSearch(encodeURIComponent(inputValue || " "));
    }, 300);

    const getProductData = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}product/page/ptype/0/10/desc/id/2/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
                { headers: authHeader() }
            );

            const products = response.data?.Index || response.data?.data || [];
            const mapped = products.map(product => ({
                value: product.id,
                label: `${product.description} - ${product.serialnumber} - ${product.partno}`,
                description: product.description,
                itemcode: product.itemcode,
                hsnsacno: product.hsnsacno,
                partno: product.partno,
                serialnumber: product.serialnumber,
                modelnumber: product.modelnumber,
                tagnumber: product.tagnumber,
                moc: product.moc,
                uom: product.unitofmeasurement || unitOptions[0].value,
            }));

            setProductOptions(mapped);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    useEffect(() => {
        getProductData();
    }, [productSearch]);

    const handleProductChange = (selectedOption) => {
        if (!selectedOption) return;
        setSelectedProduct(selectedOption);

        const newRow = {
            description: selectedOption.description,
            value: selectedOption.value,
            label: selectedOption.label,
            quantity: '',
            partno: selectedOption.partno,
            hsnsacno: selectedOption.hsnsacno,
            itemcode: selectedOption.itemcode,
            serialnumber: selectedOption.serialnumber,
            modelnumber: selectedOption.modelnumber,
            tagnumber: selectedOption.tagnumber,
            moc: selectedOption.moc,
            uom: selectedOption.uom || unitOptions[0].value,
            productremarks: "",
            currency: selectedCurrency || currencyOptions[0].value,
        };

        setRows([...rows, newRow]);
    };

    const handleRowChange = (index, key, value) => {
        const updatedRows = [...rows];
        updatedRows[index][key] = value;
        setRows(updatedRows);
    };

    const handleQuantityChange = (index, value) => {
        const updatedRows = [...rows];
        updatedRows[index].quantity = value;
        setRows(updatedRows);
    };

    const removeRow = (index) => {
        const updatedRows = [...rows];
        updatedRows.splice(index, 1);
        setRows(updatedRows);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const {
            customer,
            customerDetails,
            customerMultipleAddress,
        } = commonFormData;

        if (!customer || !customerMultipleAddress) {
            Swal.fire({
                icon: 'warning',
                title: 'Please fill the required details!',
                text: 'All fields marked with an asterisk are required.',
            });
            return;
        }

        if (rows.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'No Products Added',
                text: 'Please add at least one product to the enquiry.',
            });
            return;
        }

        const enquiryItems = rows.map(row => ({
            id: row.id,
            product: { id: row.value },
            quantity: row.quantity,
            itemcode: row.itemcode || '',
            hsnsacno: row.hsnsacno || '',
            partno: row.partno || '',
            uom: row.uom || '',
            serialnumber: row.serialnumber || '',
            modelnumber: row.modelnumber || '',
            tagnumber: row.tagnumber || '',
            productremarks: row.productremarks || '',
            moc: row.moc || '',
            currency: row.currency,
        }));

        const formData = {
            id,
            status,
            enquiryItems,
            enquirytype: "Pump Spare",
            enquirycategory: "International",
            customer: { id: customer.value },
            contactno: customerDetails.contactno,
            contactperson: customerDetails.contactperson,
            email: customerDetails.email,
            customeraddress: { id: customerMultipleAddress?.value },
            enquirydate,
            enquiryno,
            customermaterialcode,
        };

        try {
            await axios.put(`${BASE_URL}enquiry/update/${id}`, formData, {
                headers: authHeader()
            });
            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Enquiry updated successfully!',
                didClose: () => navigate("/salesEnquiryIndex/1")
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to update enquiry.'
            });
        }
    };

    if (loading) {
        return (
            <div className="container text-center py-5">
                <Spinner animation="border" />
                <p>Loading enquiry data...</p>
            </div>
        );
    }

    return (
        <div className="container">
            <Form onSubmit={handleSubmit}>
                <Row>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Enquiry Number:</Form.Label>
                            <Form.Control
                                type="text"
                                value={enquiryno}
                                readOnly
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Enquiry Date *</Form.Label>
                            <Form.Control
                                type="date"
                                value={enquirydate}
                                onChange={e => setEnquiryDate(e.target.value)}
                                required
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <CommonSalesEnquiryFields
                    onDataChange={setCommonFormData}
                    initialData={commonFormData}
                    isEditMode={true}
                />

                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group controlId="enquiryno">
                            <Form.Label>Customer Material code</Form.Label>
                            <Form.Control
                                type="text"
                                value={customermaterialcode}
                                onChange={(e) => setcustomermaterialcode(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md="6">
                        <Form.Group controlId="currency">
                            <Form.Label>Currency</Form.Label>
                            <Select
                                value={currencyOptions.find(option => option.value === selectedCurrency)}
                                onChange={(selectedOption) => {
                                    setSelectedCurrency(selectedOption.value);
                                    const updatedRows = rows.map(row => ({
                                        ...row,
                                        currency: selectedOption.value
                                    }));
                                    setRows(updatedRows);
                                }}
                                options={currencyOptions}
                                placeholder="Select Currency"
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group controlId="productSelect">
                    <Form.Label>Select a Product</Form.Label>
                    <Select
                        value={selectedProduct}
                        onInputChange={handleProductSearchChange}
                        onChange={handleProductChange}
                        options={productOptions}
                        placeholder="Search or select product"
                        isClearable
                    />
                </Form.Group>

                <div className="table-responsive mt-3">
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Product Description</th>
                                <th>Serial No.</th>
                                <th>Model No.</th>
                                <th>Tag No.</th>
                                <th>Part No.</th>
                                <th>MOC</th>
                                <th>HSN Code</th>
                                <th>Product Code</th>
                                <th>Quantity</th>
                                <th>UOM</th>
                                <th>Currency</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        {row.description}
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={row.productremarks}
                                            onChange={(e) => handleRowChange(index, 'productremarks', e.target.value)}
                                            placeholder="Remarks"
                                        />
                                    </td>
                                    <td>
                                        {row.serialnumber}
                                    </td>
                                    <td>
                                        {row.modelnumber}
                                    </td>
                                    <td>
                                        {row.tagnumber}
                                    </td>
                                    <td>
                                        {row.partno}
                                    </td>
                                    <td>
                                        {row.moc}
                                    </td>
                                    <td>
                                        {row.hsnsacno}
                                    </td>
                                    <td>
                                        {row.itemcode}
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            value={row.quantity}
                                            onChange={(e) => handleQuantityChange(index, e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <Select
                                            className='uom'
                                            value={unitOptions.find(option => option.value === row.uom)}
                                            onChange={(selected) => handleRowChange(index, 'uom', selected.value)}
                                            options={unitOptions}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menuPortal: base => ({ ...base, zIndex: 9999 })
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <Select
                                            className='uom'
                                            value={currencyOptions.find(option => option.value === row.currency)}
                                            onChange={(selected) => handleRowChange(index, 'currency', selected.value)}
                                            options={currencyOptions}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menuPortal: base => ({ ...base, zIndex: 9999 })
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm"
                                            onClick={() => removeRow(index)}
                                        >
                                            <IoMdClose />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>

                <div className="d-flex justify-content-between mt-3">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate(-1)}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                    >
                        UPDATE ENQUIRY
                    </button>
                </div>
            </Form>
        </div>
    );
}

export default EditPumpInternationalSalesEnquiry;
